import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Compass, Eye, HandHeart, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LegalPage, LegalSection } from "@/components/layout/LegalPage";
import { useCompanySettings } from "@/context/SettingsContext";

export default function AboutUs() {
  const navigate = useNavigate();
  const { settings } = useCompanySettings();
  return (
    <LegalPage
      title="About Us"
      subtitle={`${settings.company_name} — a better hiring experience for India's workforce.`}
      testId="about-page"
    >
      <LegalSection title="Who We Are">
        <p>
          {settings.company_name} is a recruitment platform built to make hiring simple, transparent and human.
          We connect job seekers with verified opportunities across banking, BPO, healthcare, e-commerce,
          insurance, telecom and more — and every application is personally handled by a real recruiter
          who guides the candidate from application to offer.
        </p>
      </LegalSection>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-5">
            <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Compass className="h-5 w-5" /></span>
            <p className="font-heading font-semibold mt-3">Our Mission</p>
            <p className="text-sm text-muted-foreground mt-1.5">To give every job seeker — fresher or experienced — a fast, respectful and completely free path to their next job.</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-5">
            <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Eye className="h-5 w-5" /></span>
            <p className="font-heading font-semibold mt-3">Our Vision</p>
            <p className="text-sm text-muted-foreground mt-1.5">To become India's most trusted recruitment platform, where transparency and real human support replace confusing, costly hiring processes.</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-5">
            <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><HandHeart className="h-5 w-5" /></span>
            <p className="font-heading font-semibold mt-3">Candidate First</p>
            <p className="text-sm text-muted-foreground mt-1.5">Apply in seconds with just your name and mobile number. No resume required to apply, live application tracking, and your recruiter is one WhatsApp message away.</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-5">
            <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Zap className="h-5 w-5" /></span>
            <p className="font-heading font-semibold mt-3">Recruiter Efficient</p>
            <p className="text-sm text-muted-foreground mt-1.5">Our recruiters work on a clean, focused operating system — clear ownership, structured statuses and full activity history — so candidates always get timely updates.</p>
          </CardContent>
        </Card>
      </div>

      <LegalSection title="Why Choose Udhyog Tech">
        <ul className="space-y-2">
          {[
            "Verified opportunities from trusted hiring brands",
            "Apply in under 30 seconds — no lengthy forms",
            "Track your application status in real time",
            "A dedicated recruiter for every application",
            "Free professional resume builder with PDF download",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>
          ))}
        </ul>
      </LegalSection>

      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="about-free-promise">
        <p className="font-heading font-semibold text-emerald-800">Our 100% FREE Promise</p>
        <p className="text-sm text-emerald-700 mt-1.5">
          {settings.company_name} is completely free for job seekers — free job applications, free resume builder,
          no registration fee, no placement charges and no hidden charges. Ever. If anyone asks you for money in
          our name, please report it to {settings.support_email || "our support team"}.
        </p>
      </div>

      <div className="flex gap-3">
        <Button className="rounded-lg" onClick={() => navigate("/jobs")} data-testid="about-find-jobs-btn">
          Find Jobs <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button variant="outline" className="rounded-lg bg-white" onClick={() => navigate("/contact")} data-testid="about-contact-btn">
          Contact Us
        </Button>
      </div>
    </LegalPage>
  );
}
